import { cn } from "@/lib/utils";
import { ROLE_LABELS } from "@/lib/labels";

// Warna kartu mengikuti hasil tap, sama seperti layar mesin absen di gerbang.
const STATUS = {
  menunggu: {
    label: "MEMPROSES",
    hint: "Kartu terbaca, menunggu catatan tersimpan…",
    pill: "bg-blue-600 text-white",
    strip: "bg-blue-50 text-blue-800",
    garis: "bg-blue-600",
  },
  hadir: {
    label: "SUDAH ABSEN",
    hint: "Silakan tempelkan kartu berikutnya",
    pill: "bg-emerald-500 text-white",
    strip: "bg-emerald-50 text-emerald-800",
    garis: "bg-emerald-500",
  },
  sudah: {
    label: "SUDAH TERCATAT",
    hint: "Sudah tercatat hari ini",
    pill: "bg-amber-500 text-white",
    strip: "bg-amber-50 text-amber-800",
    garis: "bg-amber-500",
  },
  ditolak: {
    label: "DITOLAK",
    hint: "Kartu tidak dapat diterima sistem",
    pill: "bg-red-600 text-white",
    strip: "bg-red-50 text-red-800",
    garis: "bg-red-600",
  },
};

const inisial = (nama) =>
  (nama || "?").trim().split(/\s+/).slice(0, 2).map((k) => k[0]).join("").toUpperCase();

const fmtJam = (d) => new Intl.DateTimeFormat("id-ID", { timeZone: "Asia/Jakarta", hour: "2-digit", minute: "2-digit" }).format(d);
const fmtTanggal = (d) => new Intl.DateTimeFormat("id-ID", { timeZone: "Asia/Jakarta", weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(d);

/**
 * Kartu Anggota Sekolah resmi — dipakai di layar pemindai (mode SCAN) dan pratinjau tab Kartu RFID.
 * Sengaja selalu terang seperti kartu cetak, walau aplikasi sedang mode gelap.
 */
export default function KartuAnggota({ anggota, sekolah, status = "hadir", waktu, perangkat, alasan, className, testId }) {
  const s = STATUS[status] || STATUS.hadir;
  const nama = anggota?.full_name || anggota?.uid || "Kartu tidak dikenal";
  const saat = waktu ? new Date(waktu) : new Date();
  const nomor = anggota?.nisn ? `NISN ${anggota.nisn}` : anggota?.nip ? `NIP ${anggota.nip}` : null;
  const registrasi = anggota?.registration_number ? `Reg. ${anggota.registration_number}` : null;
  const peran = anggota?.role ? ROLE_LABELS[anggota.role] || anggota.role : null;
  const kelas = anggota?.kelas ? (anggota.role === "siswa" ? `Kelas ${anggota.kelas}` : `Wali kelas ${anggota.kelas}`) : null;

  return (
    <div
      className={cn("mx-auto w-full max-w-[23rem] overflow-hidden rounded-2xl bg-white text-left text-slate-900 shadow-2xl ring-1 ring-black/10", className)}
      data-testid={testId || "kartu-anggota"}
    >
      <div className="flex items-start justify-between gap-3 bg-gradient-to-r from-[#0B3D91] to-[#1E56C8] px-4 py-3 text-white">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/70">Kartu Anggota Sekolah</p>
          <p className="font-brand truncate text-sm font-extrabold leading-snug">{sekolah || "SIST"}</p>
        </div>
        <span className="font-brand shrink-0 text-lg font-extrabold leading-none">
          SIST<span className="text-[#FF4D5A]">.</span>
        </span>
      </div>

      <div className="flex gap-3 px-4 pt-4">
        <div className="grid h-24 w-20 shrink-0 place-items-center rounded-xl border border-slate-200 bg-slate-100">
          <span className="font-brand text-2xl font-extrabold text-[#0B3D91]">{inisial(anggota?.full_name)}</span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-lg font-black leading-tight break-words">{nama}</p>
          {nomor && <p className="mt-0.5 font-mono text-xs text-slate-600">{nomor}</p>}
          {registrasi && <p className="font-mono text-xs text-slate-600">{registrasi}</p>}
          <div className="mt-1.5 flex flex-wrap gap-1">
            {peran && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700">{peran}</span>}
            {kelas && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700">{kelas}</span>}
          </div>
        </div>
      </div>

      <div className="px-4 pt-3">
        <div className={cn("inline-flex w-full items-center justify-center rounded-xl px-3 py-2.5 text-base font-extrabold tracking-wide", s.pill)}>
          {s.label}
        </div>
      </div>

      <dl className="mx-4 mt-3 grid grid-cols-2 gap-x-3 gap-y-1 rounded-xl bg-slate-50 p-3 text-xs">
        <div>
          <dt className="text-slate-500">Jam absen</dt>
          <dd className="font-semibold tabular-nums">{fmtJam(saat)} WIB</dd>
        </div>
        <div>
          <dt className="text-slate-500">Perangkat</dt>
          <dd className="truncate font-semibold">{perangkat || "Pemindai"}</dd>
        </div>
        <div className="col-span-2">
          <dt className="text-slate-500">Tanggal</dt>
          <dd className="font-semibold">{fmtTanggal(saat)}</dd>
        </div>
        {alasan && (
          <div className="col-span-2">
            <dt className="text-slate-500">Keterangan</dt>
            <dd className="font-medium text-red-700">{alasan}</dd>
          </div>
        )}
      </dl>

      <div className={cn("mt-3 flex items-center justify-between gap-2 px-4 py-2.5 text-[11px] font-semibold", s.strip)}>
        <span className="truncate">{s.hint}</span>
        {anggota?.uid && <span className="shrink-0 font-mono opacity-70">{anggota.uid}</span>}
      </div>
      <div className={cn("h-1.5 w-full", s.garis)} aria-hidden />
    </div>
  );
}
